import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { LedgerPicker, useLedgerOptions } from '../LedgerPickers';

// ---------------------------------------------------------------------------
// Shared Inventory UI kit - mirrors budgeting/BudgetingShared.js and the
// other ledger kits so Stewardship reads as one product.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  // The Laravel gateway forwards the ledger's per-field validation detail in
  // `errors` (see LedgerGatewayController) - surface it so a bare
  // "validation failed" message still tells the user which field to fix.
  const details = data?.errors;
  if (!details) return message;
  const flat = Array.isArray(details)
    ? details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d)))
    : Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return flat.length ? `${message}: ${flat.join('; ')}` : message;
};

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Document statuses (verified against InventoryTransactionService):
// DRAFT -> APPROVED -> POSTED, with REVERSED via reverse + reason.
const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  REVERSED: { label: 'Reversed', color: 'warning', Icon: VoidIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const InventoryStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

export const itemIdOf = (i) => i?.item_id ?? i?.id;
export const itemLabelOf = (i) =>
  `${i?.item_code ? `${i.item_code} - ` : ''}${i?.item_name || ''}`.trim() || (itemIdOf(i) ? `Item ${itemIdOf(i)}` : '');

// Inventory-scoped item picker (reads through the 'inventory' gateway
// section, so stewards only need ledger/inventory - unlike ProductPicker in
// LedgerPickers.js which reads through 'gl' for journal lines).
export const InventoryItemPicker = (props) => {
  const { options, loading } = useLedgerOptions('inventory-items-scoped', () =>
    ledgerAPI.getInvItems({ limit: 100 }).then((res) =>
      unwrapList(res).map((i) => ({ value: itemIdOf(i), label: itemLabelOf(i) }))
    )
  );
  return <LedgerPicker label="Item" {...props} options={options} loading={loading} />;
};


